import { ApolloError } from "apollo-boost";

export type RequestState<T, E = ApolloError> =
  | RequestStateInitial
  | RequestStateLoading
  | RequestStateLoaded<T>
  | RequestStateError<E>;

interface RequestStateInitial {
  type: "initial";
}
export function RequestStateInitial(): RequestStateInitial {
  return {
    type: "initial",
  };
}

interface RequestStateLoading {
  type: "loading";
}
export function RequestStateLoading(): RequestStateLoading {
  return {
    type: "loading",
  };
}

interface RequestStateLoaded<T> {
  type: "loaded";
  resource: T;
}
export function RequestStateLoaded<T>(resource: T): RequestStateLoaded<T> {
  return {
    type: "loaded",
    resource,
  };
}

interface RequestStateError<E> {
  type: "error";
  error: E;
}
export function RequestStateError<E>(error: E): RequestStateError<E> {
  return {
    type: "error",
    error,
  };
}

export function isRequestStarted<T, E>(
  state: RequestState<T, E>
): state is Exclude<RequestState<T, E>, RequestStateInitial> {
  return state.type !== "initial";
}

export function isRequestLoading<T, E>(
  state: RequestState<T, E>
): state is RequestStateLoading {
  return state.type === "loading";
}

export function isRequestPending<T, E>(
  state: RequestState<T, E>
): state is RequestStateInitial | RequestStateLoading {
  return !isRequestStarted(state) || isRequestLoading(state);
}

export function isRequestLoaded<T, E>(
  state: RequestState<T, E>
): state is RequestStateLoaded<T> {
  return state.type === "loaded";
}

export function isRequestError<T, E>(
  state: RequestState<T, E>
): state is RequestStateError<E> {
  return state.type === "error";
}

export function getRequestResource<T, E>(state: RequestStateLoaded<T>): T;
export function getRequestResource<T, E>(
  state: Exclude<RequestState<T, E>, RequestStateLoaded<T>>
): null;
export function getRequestResource<T, E>(state: RequestState<T, E>): T | null;
export function getRequestResource<T, E>(state: RequestState<T, E>): T | null {
  if (isRequestLoaded(state)) {
    return state.resource;
  }
  return null;
}

export function getRequestError<T, E>(state: RequestStateError<E>): E;
export function getRequestError<T, E>(
  state: Exclude<RequestState<T, E>, RequestStateError<E>>
): null;
export function getRequestError<T, E>(state: RequestState<T, E>): E | null;
export function getRequestError<T, E>(state: RequestState<T, E>): E | null {
  if (isRequestError(state)) {
    return state.error;
  }
  return null;
}

export function getApolloErrorCodes<T>(
  state: RequestStateError<ApolloError>
): string[];
export function getApolloErrorCodes<T>(
  state: Exclude<RequestState<T, ApolloError>, RequestStateError<ApolloError>>
): null;
export function getApolloErrorCodes<T>(
  state: RequestState<T, ApolloError>
): string[] | null;
export function getApolloErrorCodes<T>(
  state: RequestState<T, ApolloError>
): string[] | null {
  if (isRequestError(state)) {
    const { graphQLErrors } = getRequestError(state);
    const codes: string[] = [];
    for (const gqlErr of graphQLErrors) {
      const code = gqlErr.extensions?.code;
      if (typeof code === "string") {
        codes.push(code);
      }
    }
    return codes;
  }
  return null;
}

// Get first apollo error code if found
export function getApolloErrorCode<T>(
  state: RequestState<T, ApolloError>
): string | null {
  const codes = getApolloErrorCodes(state);
  if (codes == null || codes.length === 0) {
    return null;
  }
  return codes[0];
}
